/**
 * Copy-to-clipboard button (APP-001 §17, §32).
 *
 * Copies a value into the clipboard without ever re-rendering the value into
 * the toast text or any analytics attribute. On success, shows a generic
 * "Copied" confirmation (never the copied value). The value is never written
 * into the DOM beyond the controlled input it already lives in.
 */
import * as React from 'react';
import { Check, Copy } from '@/components/icons';
import { IconButton } from '@/components/ui/icon-button';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';

export interface CopyButtonProps {
  /** Value to place on the clipboard. */
  value: string;
  /** Accessible label (defaults to "Copy to clipboard"). */
  label?: string;
  /** Optional toast title shown on success (defaults to "Copied"). */
  successMessage?: string;
  className?: string;
  disabled?: boolean;
  /**
   * Optional callback fired after the copy attempt. Receives ONLY a boolean
   * success flag — never the copied value — so callers can fire analytics
   * without re-handling the value. Used by APP-004 to emit
   * `api_key_copy_requested`.
   */
  onCopied?: (success: boolean) => void;
}

export function CopyButton({
  value,
  label = 'Copy to clipboard',
  successMessage = 'Copied',
  className,
  disabled,
  onCopied,
}: CopyButtonProps) {
  const [copied, setCopied] = React.useState(false);
  const { toast } = useToast();

  async function onCopy() {
    if (disabled) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        // Legacy fallback for non-secure contexts; never used in production
        // (production is https). The textarea is never appended to the DOM
        // visibly and is removed immediately after selection.
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2_000);
      // IMPORTANT: never pass `value` into the toast description or title.
      toast({ title: successMessage });
      onCopied?.(true);
    } catch {
      toast({ title: 'Copy failed', description: 'Copy this value manually.', variant: 'destructive' });
      onCopied?.(false);
    }
  }

  return (
    <IconButton
      type="button"
      label={label}
      variant="outline"
      size="sm"
      className={cn('font-mono', className)}
      onClick={onCopy}
      disabled={disabled}
      data-testid="copy-button"
    >
      {copied ? <Check /> : <Copy />}
    </IconButton>
  );
}
